import React, { useState } from 'react';
import {
  Button, Col, Modal, Row, Table,
} from 'react-bootstrap';
import { faTriangleExclamation, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { compact, isPlainObject, isString } from 'lodash-es';
import PropTypes from 'prop-types';

import { approveSurveyFeedback, removeTeamSizeRestriction } from '../../../../bridge';
import { __ } from '../../../../helpers/translations';
import RespondentCard from '../components/RespondentCard';

import AddParticipantModal from './AddParticipantModal';
import EditResponseModal from './EditResponseModal';

function TeamParticipantsModal({
  teamName,
  respondents,
  surveyId,
  surveyStatus,
  onChanged,
  onClose,
  releaseMode,
  teamCompleted,
  notApproved,
  ignoreTeamSizeRestriction,
}) {
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showEditResponseModal, setShowEditResponseModal] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editRespondent, setEditRespondent] = useState(null);
  const [editTeamMemberId, setEditTeamMemberId] = useState(null);
  const [showConfirmApproval, setShowConfirmApproval] = useState(false);

  const completedCount = respondents.filter(({ status }) => status === 'completed').length;

  const getPeerResponsesByField = (field, respondent) => Object.keys(respondent.response[field] || {})
    .map((respondentId) => ({
      feedback: isPlainObject(respondent.response[field])
        ? respondent.response[field][respondentId]
        : null,
      teamMember: respondents.find((r) => r.respondentId === respondentId),
      field,
    }))
    .filter((responses) => !!responses.teamMember && !!responses.feedback);

  const getResponsesByField = (field, respondent) => (isString(respondent.response[field])
    ? respondent.response[field]
    : null);

  const peerFeedback = respondents.map((respondent) => ({
    respondent,
    response: [
      ...getPeerResponsesByField('Qualitative', respondent),
      ...getPeerResponsesByField('QualitativePeer', respondent),
    ],
  }))
    .filter(({ response }) => response.length > 0);

  const teamFeedback = compact(respondents.map((respondent) => {
    const qualitativeResponse = getResponsesByField('Qualitative', respondent);
    const qualitativeTeamResponse = getResponsesByField('QualitativeTeam', respondent);
    if (!qualitativeResponse && !qualitativeTeamResponse) {
      return null;
    }
    return {
      respondent,
      response: qualitativeResponse || qualitativeTeamResponse,
      field: qualitativeResponse ? 'Qualitative' : 'QualitativeTeam',
    };
  }));

  const confidentialFeedback = respondents
    .filter(({ response }) => !!response.Confidential)
    .map((respondent) => ({
      respondent,
      response: respondent.response.Confidential,
    }));

  const onApprove = async () => {
    await approveSurveyFeedback(surveyId, teamName);
    await onChanged();
    onClose();
  };

  return (
    <>
      <Modal
        show
        centered
        dialogClassName="modal-xl"
        onHide={onClose}
        scrollable
      >
        <Modal.Header>
          <Modal.Title>{`Team ${teamName} Participants`}</Modal.Title>
          <FontAwesomeIcon
            icon={faUserPlus}
            onClick={() => setShowAddParticipantModal(true)}
            fontSize={25}
            className="cursor-pointer my-3 pe-2"
          />
        </Modal.Header>
        <Modal.Body>
          <Row className="pb-4">
            {respondents && (
              respondents.map((respondent) => (
                <Col
                  key={`team-participants-modal-respondent-${respondent.respondentId}`}
                  className="px-1 col-same-height"
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  xl={3}
                >
                  <RespondentCard
                    surveyId={surveyId}
                    surveyStatus={surveyStatus}
                    respondent={respondent}
                    onChanged={onChanged}
                  />
                </Col>
              ))
            )}
          </Row>
          {respondents.length < 3 && !ignoreTeamSizeRestriction && (
            <div className="text-danger pt-2" style={{ fontSize: '1.1em' }}>
              <hr />
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span className="px-1">{__('G144')}</span>
              <div style={{ fontSize: '0.8em' }} className="pt-2 text-muted">
                {__('G148', null, [3 - respondents.length])}
                {' '}
                {__('G151')}
              </div>
              <div>
                <br />
                <Button
                  variant="danger"
                  onClick={async () => {
                    await removeTeamSizeRestriction(surveyId);
                    window.location.reload();
                  }}
                >
                  {__('G331')}
                </Button>
              </div>
            </div>
          )}
          {surveyStatus === 'ended' && completedCount < 3 && !ignoreTeamSizeRestriction && (
            <div className="text-danger pt-2" style={{ fontSize: '1.1em' }}>
              <hr />
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span className="px-1">{__('G145')}</span>
              <div style={{ fontSize: '0.8em' }} className="pt-2 text-muted">
                {__('G149', null, [completedCount, respondents.length])}
              </div>
              <div>
                <br />
                <Button
                  variant="danger"
                  onClick={async () => {
                    await removeTeamSizeRestriction(surveyId);
                    window.location.reload();
                  }}
                >
                  {__('G331')}
                </Button>
              </div>
            </div>
          )}
          {teamCompleted && notApproved && (
          <div className="text-danger pt-2" style={{ fontSize: '1.1em' }}>
            <hr />
            <FontAwesomeIcon icon={faTriangleExclamation} />
            <span className="px-1">{__('G146')}</span>
            <div style={{ fontSize: '0.8em' }} className="pt-2 text-muted">
              {__('G150')}
              {' '}
              {__('G151')}
            </div>
          </div>
          )}
          {confidentialFeedback.length > 0 && (
            <div>
              <hr />
              <div className="text-warning pt-2" style={{ fontSize: '1.1em' }}>
                <FontAwesomeIcon icon={faTriangleExclamation} />
                <span className="px-1">{__('G147')}</span>
              </div>
              <Table size="sm" style={{ fontSize: '0.8em' }}>
                <thead>
                  <tr>
                    <th width="15%">{__('G152')}</th>
                    <th width="85%">{__('G153')}</th>
                  </tr>
                </thead>
                <tbody>
                  {confidentialFeedback.map(({ respondent, response }) => (
                    <tr key={`team-participants-modal-confidential-feedback-${respondent.respondentId}`}>
                      <td>{`${respondent.firstName} ${respondent.lastName}`}</td>
                      <td className="fst-italic">{`${response}`}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {teamFeedback.length > 0 && (
            <div>
              <hr />
              <div className="pt-2" style={{ fontSize: '1.1em' }}>
                <span className="px-1">{__('G154')}</span>
              </div>
              <Table size="sm" style={{ fontSize: '0.8em' }}>
                <thead>
                  <tr>
                    <th width="15%">{__('G152')}</th>
                    <th width="85%">{__('G153')}</th>
                    {releaseMode === 'review' && notApproved
                        && (<th width="5%">{__('G155')}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {teamFeedback.map(({ respondent, response, field }) => (
                    <tr key={`team-participants-modal-team-feedback-${respondent.respondentId}`}>
                      <td>{`${respondent.firstName} ${respondent.lastName}`}</td>
                      <td className="fst-italic">{`${response}`}</td>
                      {releaseMode === 'review' && notApproved && (
                      <td>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="btn-text"
                          onClick={() => {
                            setEditField(field);
                            setEditTeamMemberId(null);
                            setEditRespondent(respondent);
                            setShowEditResponseModal(true);
                          }}
                        >
                          {__('G156')}
                        </Button>
                      </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {peerFeedback.length > 0 && (
            <div>
              <hr />
              <div className="pt-2" style={{ fontSize: '1.1em' }}>
                <span className="px-1">{__('G28')}</span>
              </div>
              <Table size="sm" style={{ fontSize: '0.8em' }}>
                <thead>
                  <tr>
                    <th width="15%">{__('G152')}</th>
                    <th width="15%">{__('G157')}</th>
                    <th width="65%">{__('G153')}</th>
                    {releaseMode === 'review' && notApproved
                        && (<th width="5%">{__('G155')}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {peerFeedback.map(({ respondent, response }) => response.map(({ teamMember, feedback, field }) => (
                    <tr key={`team-participants-modal-peer-feedback-${respondent.respondentId}-${teamMember.respondentId}-${field}`}>
                      <td>{`${respondent.firstName} ${respondent.lastName}`}</td>
                      <td>{`${teamMember.firstName} ${teamMember.lastName}`}</td>
                      <td className="fst-italic">{`${feedback}`}</td>
                      {releaseMode === 'review' && notApproved && (
                      <td>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="btn-text"
                          onClick={() => {
                            setEditField(field);
                            setEditTeamMemberId(teamMember.respondentId);
                            setEditRespondent(respondent);
                            setShowEditResponseModal(true);
                          }}
                        >
                          {__('G156')}
                        </Button>
                      </td>
                      )}
                    </tr>
                  )))}
                </tbody>
              </Table>
            </div>
          )}
          <br />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>
            {__('G167')}
          </Button>
          {releaseMode === 'review' && notApproved && (
            <Button
              variant="primary"
              onClick={() => {
                if (teamCompleted || surveyStatus === 'ended') {
                  onApprove();
                } else {
                  setShowConfirmApproval(true);
                }
              }}
            >
              {__('G158')}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {showAddParticipantModal && (
        <AddParticipantModal
          surveyId={surveyId}
          onCancel={() => setShowAddParticipantModal(false)}
          onChanged={async () => {
            setShowAddParticipantModal(false);
            await onChanged();
          }}
          teamName={teamName}
        />
      )}
      {showEditResponseModal && (
        <EditResponseModal
          respondent={editRespondent}
          responseKey={editField}
          teamMemberId={editTeamMemberId}
          onClose={() => setShowEditResponseModal(false)}
          onChanged={onChanged}
        />
      )}
      {showConfirmApproval && (
        <Modal show>
          <Modal.Header>
            <Modal.Title>{__('G159')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {__('G160')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowConfirmApproval(false)}>
              {__('G106')}
            </Button>
            <Button onClick={() => {
              setShowConfirmApproval(false);
              onApprove();
            }}
            >
              {__('G107')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

TeamParticipantsModal.propTypes = {
  respondents: PropTypes.array,
  teamName: PropTypes.string,
  surveyId: PropTypes.string,
  onChanged: PropTypes.func,
  onClose: PropTypes.func,
  releaseMode: PropTypes.string,
  teamCompleted: PropTypes.bool,
  notApproved: PropTypes.bool,
  surveyStatus: PropTypes.string,
  ignoreTeamSizeRestriction: PropTypes.bool,
};

export default TeamParticipantsModal;
